import { useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import Idle from 'react-idle';

import PinDrawerWithoutButton from './components/PinDrawerWithoutButton/PinDrawerWithoutButton';
import PinModalWithoutButton from './components/PinModalWithoutButton/PinModalWithoutButton';
import ScrollToTop from './components/TradeItem/ScrollToTop';
import ResetPin from './pages/Account/Profile/Modals/ResetPin';

import { RemoveTrailingSlash } from './components/RemoveTrailingSlash/RemoveTrailingSlash';
import useMediaQuery from './Hooks/useMediaQuery';
import { PrivateWrapper } from './PrivateWrapper';

import { routePaths } from './routePaths';
import { resetLoginPin } from './services/AccountApi/profileApi';
import { PIN_VERIFICATION_TYPES, checkOnboardingComplete } from './utils/constant';
import { formatePhoneNumber } from './utils/formatPhoneNumber';
import { toastNotify } from './utils/toastNotify';
import IncompleteOnboarding from './components/IncompleteOnboarding/IncompleteOnboarding';
import BranchIoRouteListener from './components/BranchIO/BranchIoRouteListener';
import { resetUserData } from './store/reducer/accountSlice';

// Routes that require an authenticated session (see routePaths.js `isAuth: true` entries).
const PROTECTED_PATH_PREFIXES = ['/account', '/portfolio', '/sign-up/linkbank'];
const SESSION_TOKEN_POLL_INTERVAL = 3000;

const RouterPath = () => {
  const [openPinModal, setOpenPinModal] = useState(false);
  const [showOnboardingPopup, setShowOnboardingPopup] = useState(false);
  const [pin, setPin] = useState();
  const [loadingModal, setLoadingModal] = useState(false);
  const [resetPin, setResetPin] = useState(false);
  const [firstTimeOpen, setFirstTimeOpen] = useState(false);
  const [alreadyShowedIncompleteOnboarding, setAlreadyShowedIncompleteOnboarding] = useState(false)
  const { userData: userProfileData, isPinEntered, counter, isProfileLoaded } = useSelector((state) => state.userData);
  const phoneNumber = ['+1', formatePhoneNumber(userProfileData?.phoneNumber)];
  const screenMD = useMediaQuery('(min-width: 1000px)');
  const location = useLocation();
  const dispatch = useDispatch();
  const hadAccessTokenRef = useRef(!!localStorage.getItem('access_token'));
  const handleForgotPin = async () => {
    setLoadingModal(true);
    try {
      const resData = await resetLoginPin();
      if (!resData.isError) {
        toastNotify('success', resData?.message);
        setOpenPinModal(false);
        setResetPin(true);
      }
    } catch (error) {
      const { response } = error;
      toastNotify('error', response?.data?.message);
    } finally {
      setLoadingModal(false);
    }
  };
  const handleVerifyPinOpenOrders = () => {
    setOpenPinModal(false);
    localStorage.setItem('restrictPage', false);
    localStorage.removeItem('tabClosingTime');
  };

  const handleCloseOnboardingpopup = () =>{
    localStorage.setItem('onboardingIncomplete',false)
    setShowOnboardingPopup(false)
  }

  useEffect(() => {
    if (localStorage.getItem('restrictPage') === 'true' && isPinEntered && localStorage.getItem('access_token') && Object.keys(userProfileData).length > 0) {
      setOpenPinModal(true);
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem('restrictPage') === 'true' && isPinEntered && localStorage.getItem('access_token') && Object.keys(userProfileData).length > 0) {
      setOpenPinModal(true);
      const blockerRoute = localStorage.getItem('blockerRoute');
      if (blockerRoute && blockerRoute !== window.location.href) {
        // Redirect back to the blockerRoute
        window.location.href = blockerRoute;
      }
    }
  }, []);

  useEffect(()=>{
    if(!localStorage.getItem('access_token') && Object.keys(userProfileData).length === 0){
      setOpenPinModal(false);
      localStorage.setItem('restrictPage','false')
    }
  },[])

  // Detects the access token disappearing (expiry, manual removal, another tab logging out).
  // Always clears the stale Redux session, and on a protected page forces an immediate
  // redirect to /login instead of leaving the user there to keep hitting the API and failing.
  useEffect(() => {
    const checkSessionToken = () => {
      const hasAccessToken = !!localStorage.getItem('access_token');

      if (hasAccessToken) {
        hadAccessTokenRef.current = true;
        return;
      }

      if (!hadAccessTokenRef.current) {
        return;
      }

      hadAccessTokenRef.current = false;
      dispatch(resetUserData());

      const currentPath = window.location.pathname;
      if (PROTECTED_PATH_PREFIXES.some((prefix) => currentPath.startsWith(prefix))) {
        localStorage.setItem('prev_pathname', currentPath);
        window.location.href = '/login';
      }
    };

    const intervalId = setInterval(checkSessionToken, SESSION_TOKEN_POLL_INTERVAL);
    window.addEventListener('storage', checkSessionToken);
    window.addEventListener('focus', checkSessionToken);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('storage', checkSessionToken);
      window.removeEventListener('focus', checkSessionToken);
    };
  }, [dispatch]);

  useEffect(()=>{
    const currentPath = location.pathname;
    const isRestrictPage = localStorage.getItem('restrictPage') === 'true';
    const isKYCVerified = userProfileData?.isKYCVerified;
    const isUsCitizen = userProfileData?.isUsCitizen;
    const isOnboardingComplete = checkOnboardingComplete(userProfileData, counter);
    const notShowingIncompleteOnboarding = [
      '/login',
      '/sign-up',
      '/sign-upAuth',
      '/sign-up/activation',
      '/sign-up/createpin',
      '/reactive-account/createpin',
      '/reactive-account/confirmpin',
      '/sign-up/confirmpin',
      '/sign-up/verification',
      '/sign-up/linkbank',
      '/not-found',
      '/went-wrong',
      '/login/enterpin',
      '/forgot-password',
      '/forgot-password/activation',
      '/forgot-password/enter-email',
      '/forgot-password/new-password',
      '/forgot-password/allset',
      '/forgot-pin',
      '/forgot-pin/request-reset',
      '/forgot-pin/create-new-pin',
      '/forgot-pin/confirm-new-pin',
      '/re-login',
      '/re-login/enterpin',
      '/legal'
    ];

    if (notShowingIncompleteOnboarding.includes(currentPath)) {
      setShowOnboardingPopup(false);
      setOpenPinModal(false)
      localStorage.setItem('restrictPage','false')
      localStorage.setItem('onboardingIncomplete', 'false');
      return; 
    }

    if(alreadyShowedIncompleteOnboarding){
      return
    }

    if(!isRestrictPage && isProfileLoaded && Object.keys(userProfileData).length > 0 && !isKYCVerified && isUsCitizen && !isOnboardingComplete){
      setShowOnboardingPopup(true);
      setAlreadyShowedIncompleteOnboarding(true)
      localStorage.setItem('onboardingIncomplete', 'true');
    } else {
      setShowOnboardingPopup(false);
      localStorage.setItem('onboardingIncomplete',false);
    }
  },[isPinEntered, location, alreadyShowedIncompleteOnboarding, counter, isProfileLoaded])
  
  useEffect(() => {
    const isOnboardingIncomplete = localStorage.getItem('onboardingIncomplete') === 'true';
    if (isOnboardingIncomplete) {
      setShowOnboardingPopup(true);
      const blockerRoute = localStorage.getItem('blockerRoute');
      if (blockerRoute && blockerRoute !== window.location.href) {
        window.location.href = blockerRoute;
      }
    }
  }, []);

  useEffect(() => {
    const handleTabClose = (event) => {
      // Cancel the event to prevent the browser from closing immediately
      localStorage.setItem('tabClosingTime', new Date().getTime());
    };
    window.addEventListener('beforeunload', handleTabClose);
    return () => {
      window.removeEventListener('beforeunload', handleTabClose);
    };
  }, []);

  useEffect(() => {
    const currentTime = new Date().getTime();
    const tabClosingTime = localStorage.getItem('tabClosingTime');
    if (tabClosingTime && isPinEntered) {
      const differenceInSeconds = (currentTime - parseInt(tabClosingTime)) / 1000;
      if (differenceInSeconds > 600) {
        setOpenPinModal(true);
        localStorage.setItem('restrictPage', true);
        localStorage.removeItem('tabClosingTime');
      }
    }
  });

  return (
  <>
      <ScrollToTop />
      <RemoveTrailingSlash />
      <BranchIoRouteListener />
      {Object.keys(userProfileData).length > 0 && (
        <Idle
          onChange={({ idle }) => {
            if (firstTimeOpen) {
              if (idle && !openPinModal) {
                setOpenPinModal(true);
                localStorage.setItem('blockerRoute', window.location.href);
                localStorage.setItem('restrictPage', true);
              }
            } else {
              setFirstTimeOpen(true);
            }
          }}
          timeout={600000}
        />
      )}
      <ResetPin
        phoneNumber={phoneNumber || ''}
        mail={userProfileData?.email || ''}
        setOpen={resetPin}
        setClose={() => {
          setResetPin(false);
          setOpenPinModal(true);
          localStorage.setItem('restrictPage', true);
        }}
      />
      <IncompleteOnboarding
         visible={showOnboardingPopup}
         onClose={handleCloseOnboardingpopup}
         setShowOnboardingPopup={setShowOnboardingPopup}
      />
      <Routes>
        {routePaths.map((route, index) => {
          const { path, component: Component, exact, isAuth } = route.mainRoute;
          return (
            <Route
              key={index}
              exact={exact}
              path={path}
              element={
                isAuth ? (
                  <PrivateWrapper>
                    <Component />
                    {Object.keys(userProfileData).length > 0 &&
                      openPinModal &&
                      (screenMD ? (
                        <PinModalWithoutButton
                          onCancel={() => {
                            setOpenPinModal(false);
                            localStorage.setItem('restrictPage', false);
                          }}
                          open={openPinModal}
                          pin={pin}
                          loading={loadingModal}
                          handleForgotPin={handleForgotPin}
                          setPin={setPin}
                          setLoading={setLoadingModal}
                          onSuccess={() => {
                            handleVerifyPinOpenOrders();
                          }}
                          isRoute
                          pinVerificationType={PIN_VERIFICATION_TYPES.LOGIN}
                        />
                      ) : (
                        Object.keys(userProfileData).length > 0 &&
                        openPinModal && (
                          <PinDrawerWithoutButton
                            onCancel={() => {
                              setOpenPinModal(false);
                              localStorage.setItem('restrictPage', false);
                            }}
                            open={openPinModal}
                            pin={pin}
                            loading={loadingModal}
                            handleForgotPin={handleForgotPin}
                            setPin={setPin}
                            setLoading={setLoadingModal}
                            onSuccess={() => {
                              handleVerifyPinOpenOrders();
                            }}
                            isRoute
                            setOpen={(value) => setOpenPinModal(value)}
                            pinVerificationType={PIN_VERIFICATION_TYPES.LOGIN}
                          />
                        )
                      ))}
                  </PrivateWrapper>
                ) : (
                  <>
                    {Object.keys(userProfileData).length > 0 &&
                      openPinModal &&
                      (screenMD ? (
                        <PinModalWithoutButton
                          onCancel={() => {
                            setOpenPinModal(false);
                            localStorage.setItem('restrictPage', false);
                          }}
                          open={openPinModal}
                          pin={pin}
                          loading={loadingModal}
                          handleForgotPin={handleForgotPin}
                          setPin={setPin}
                          setLoading={setLoadingModal}
                          onSuccess={() => {
                            handleVerifyPinOpenOrders();
                          }}
                          isRoute
                          pinVerificationType={PIN_VERIFICATION_TYPES.LOGIN}
                        />
                      ) : (
                        Object.keys(userProfileData).length > 0 &&
                        openPinModal && (
                          <PinDrawerWithoutButton
                            onCancel={() => {
                              setOpenPinModal(false);
                              localStorage.setItem('restrictPage', false);
                            }}
                            open={openPinModal}
                            pin={pin}
                            loading={loadingModal}
                            handleForgotPin={handleForgotPin}
                            setPin={setPin}
                            setLoading={setLoadingModal}
                            onSuccess={() => {
                              handleVerifyPinOpenOrders();
                            }}
                            isRoute
                            setOpen={(value) => setOpenPinModal(value)}
                            pinVerificationType={PIN_VERIFICATION_TYPES.LOGIN}
                          />
                        )
                      ))}
                    <Component />
                  </>
                )
              }
            >
              {route.subRoute &&
                route.subRoute.map((subRout1, index) => {
                  const { path, component: Component, exact, isAuth } = subRout1;
                  return (
                    <Route
                      key={index}
                      exact={exact}
                      path={path}
                      element={
                        isAuth ? (
                          <PrivateWrapper>
                            <Component />
                            {Object.keys(userProfileData).length > 0 &&
                              openPinModal &&
                              (screenMD ? (
                                <PinModalWithoutButton
                                  onCancel={() => {
                                    setOpenPinModal(false);
                                    localStorage.setItem('restrictPage', false);
                                  }}
                                  open={openPinModal}
                                  pin={pin}
                                  loading={loadingModal}
                                  handleForgotPin={handleForgotPin}
                                  setPin={setPin}
                                  setLoading={setLoadingModal}
                                  onSuccess={() => {
                                    handleVerifyPinOpenOrders();
                                  }}
                                  isRoute
                                  pinVerificationType={PIN_VERIFICATION_TYPES.LOGIN}
                                />
                              ) : (
                                Object.keys(userProfileData).length > 0 &&
                                openPinModal && (
                                  <PinDrawerWithoutButton
                                    onCancel={() => {
                                      setOpenPinModal(false);
                                      localStorage.setItem('restrictPage', false);
                                    }}
                                    open={openPinModal}
                                    pin={pin}
                                    loading={loadingModal}
                                    handleForgotPin={handleForgotPin}
                                    setPin={setPin}
                                    setLoading={setLoadingModal}
                                    onSuccess={() => {
                                      handleVerifyPinOpenOrders();
                                    }}
                                    isRoute
                                    setOpen={(value) => setOpenPinModal(value)}
                                    pinVerificationType={PIN_VERIFICATION_TYPES.LOGIN}
                                  />
                                )
                              ))}
                          </PrivateWrapper>
                        ) : (
                          <>
                            {Object.keys(userProfileData).length > 0 &&
                              openPinModal &&
                              (screenMD ? (
                                <PinModalWithoutButton
                                  onCancel={() => {
                                    setOpenPinModal(false);
                                    localStorage.setItem('restrictPage', false);
                                  }}
                                  open={openPinModal}
                                  pin={pin}
                                  loading={loadingModal}
                                  handleForgotPin={handleForgotPin}
                                  setPin={setPin}
                                  setLoading={setLoadingModal}
                                  onSuccess={() => {
                                    handleVerifyPinOpenOrders();
                                  }}
                                  isRoute
                                  pinVerificationType={PIN_VERIFICATION_TYPES.LOGIN}
                                />
                              ) : (
                                Object.keys(userProfileData).length > 0 &&
                                openPinModal && (
                                  <PinDrawerWithoutButton
                                    onCancel={() => {
                                      setOpenPinModal(false);
                                      localStorage.setItem('restrictPage', false);
                                    }}
                                    open={openPinModal}
                                    pin={pin}
                                    loading={loadingModal}
                                    handleForgotPin={handleForgotPin}
                                    setPin={setPin}
                                    setLoading={setLoadingModal}
                                    onSuccess={() => {
                                      handleVerifyPinOpenOrders();
                                    }}
                                    isRoute
                                    setOpen={(value) => setOpenPinModal(value)}
                                    pinVerificationType={PIN_VERIFICATION_TYPES.LOGIN}
                                  />
                                )
                              ))}
                            <Component />
                          </>
                        )
                      }
                    />
                  );
                })}
            </Route>
          );
        })}
        <Route path='*' element={<Navigate to='/not-found' replace />} />
      </Routes>
    </>
  );
};

export default RouterPath;
